import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Modal } from '../../components/ui'
import { ArrowRight, Trophy, Users, Shield, Zap, Target, Crown, CheckCircle, Smartphone, Play, Globe, Gamepad2 } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { DEMO_TOURNAMENT_ID, DEMO_ESPORTS_ID } from '../../data/demoData'

export const LandingPage = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { t } = useTranslation()
    const [showVideo, setShowVideo] = useState(false)

    useEffect(() => {
        document.title = "ProPoule & ProRacket | De #1 Gratis WK Poule 2026 & Racket Toernooiplanner"
        const metaDesc = document.querySelector('meta[name="description"]')
        if (metaDesc) {
            metaDesc.setAttribute("content", "Maak gratis een prachtige WK Poule 2026 aan met ProPoule, of plan een dynamisch padel & tennis King of the Court toernooi met ProRacket. Start direct!")
        }

        // Auto scroll if URL contains hash on mount
        if (window.location.hash) {
            const id = window.location.hash.substring(1)
            setTimeout(() => {
                const el = document.getElementById(id)
                if (el) el.scrollIntoView({ behavior: 'smooth' })
            }, 250)
        }
    }, [])

    const scrollToBrand = (id) => {
        const el = document.getElementById(id)
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' })
        } else {
            navigate(`/#${id}`)
        }
    }

    const handleStart = () => {
        if (user) {
            navigate('/admin/dashboard')
        } else {
            navigate('/login')
        }
    }

    return (
        <div className="min-h-screen font-sans text-white bg-[#0a0a0a] selection:bg-orange-500 selection:text-white overflow-x-hidden">

            {/* 0. NAVIGATION HEADER */}
            <nav className="absolute top-0 w-full z-50 px-6 py-4 md:py-6">
                <div className="container mx-auto flex justify-between items-center">
                    {/* Logo */}
                    <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate('/')}>
                        <img
                            src="/images/logo.svg"
                            alt="ProPoule Logo"
                            className="w-8 h-8 md:w-10 md:h-10 transform group-hover:rotate-12 transition-transform"
                        />
                        <span className="font-black text-2xl md:text-3xl tracking-tighter uppercase italic select-none">
                            <span className="text-white">PRO</span>
                            <span className="text-orange-500 ml-[0.15rem]">POULE</span>
                        </span>
                    </div>

                    {/* Central Brand Navigator Pill */}
                    <div className="hidden md:flex items-center gap-6 bg-white/5 border border-white/10 rounded-2xl px-6 py-2 backdrop-blur-lg">
                        <button onClick={() => scrollToBrand('propoule')} className="text-xs font-bold text-slate-300 hover:text-orange-400 transition-colors uppercase tracking-widest">⚽ ProPoule</button>
                        <span className="text-white/20">|</span>
                        <button onClick={() => navigate('/wkpoule')} className="text-xs font-bold text-slate-300 hover:text-orange-400 transition-colors uppercase tracking-widest">🏆 WK Poule</button>
                        <span className="text-white/20">|</span>
                        <button onClick={() => navigate('/e-sports')} className="text-xs font-bold text-slate-300 hover:text-purple-400 transition-colors uppercase tracking-widest">🎮 ProPoule Esports</button>
                        <span className="text-white/20">|</span>
                        <button onClick={() => navigate('/proracket')} className="text-xs font-bold text-slate-300 hover:text-green-400 transition-colors uppercase tracking-widest">🎾 ProPoule Racket</button>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/login')}
                            className="text-gray-300 hover:text-white font-medium transition-colors hidden md:block"
                        >
                            {t('landing.nav_login')}
                        </button>
                        <Button
                            onClick={handleStart}
                            className="bg-white/10 hover:bg-white/20 text-white backdrop-blur-md border border-white/10 rounded-xl px-6"
                        >
                            {t('landing.nav_start')}
                        </Button>
                    </div>
                </div>
            </nav>

            {/* 1. HERO SECTION - High Impact */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                {/* Background Layer */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/80 to-[#0a0a0a] z-10"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-transparent to-[#0a0a0a]/50 z-10"></div>
                    <img
                        src="/images/soccer crowd.webp"
                        alt="Stadium Atmosphere"
                        className="w-full h-full object-cover scale-110 animate-slow-pan opacity-60"
                    />
                </div>

                {/* Content Layer */}
                <div className="container mx-auto px-6 relative z-20 pt-20 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-sm font-medium animate-fade-in-down mb-6">
                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                        {t('landing.hero_badge')}
                    </div>

                    <h1 className="text-6xl md:text-8xl font-black tracking-tighter mb-6 leading-tight animate-slide-up">
                        {t('landing.hero_title_1')} <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-red-600 drop-shadow-[0_0_15px_rgba(249,115,22,0.5)]">
                            {t('landing.hero_title_2')}
                        </span> <br />
                        {t('landing.hero_title_3')}
                    </h1>

                    <p className="text-xl md:text-2xl text-gray-300 max-w-2xl mx-auto mb-10 font-light leading-relaxed animate-slide-up delay-100">
                        {t('landing.hero_sub')}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-up delay-200 w-full max-w-4xl mx-auto flex-wrap">
                        <Button
                            onClick={handleStart}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg rounded-2xl shadow-[0_0_30px_rgba(249,115,22,0.4)] hover:shadow-[0_0_50px_rgba(249,115,22,0.6)] transition-all transform hover:-translate-y-1 border-0 min-w-[200px]"
                        >
                            {t('landing.btn_start_free')}
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>

                        <button
                            onClick={() => navigate(`/live/${DEMO_TOURNAMENT_ID}`)}
                            className="group relative px-6 py-6 bg-white/5 hover:bg-white/10 backdrop-blur-md rounded-2xl font-bold text-white border border-white/10 shadow-lg overflow-hidden transition-all hover:scale-105 active:scale-95 min-w-[200px] text-left"
                        >
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400 group-hover:text-white group-hover:bg-blue-500 transition-colors">
                                    <Globe size={20} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-400 uppercase tracking-wider">Live Demo</span>
                                    <span className="text-sm font-black">FOOTBALL</span>
                                </div>
                            </div>
                        </button>

                        <button
                            onClick={() => navigate(`/live/${DEMO_ESPORTS_ID}`)}
                            className="group relative px-6 py-6 bg-purple-900/20 hover:bg-purple-900/40 backdrop-blur-md rounded-2xl font-bold text-white border border-purple-500/30 shadow-lg overflow-hidden transition-all hover:scale-105 active:scale-95 min-w-[200px] text-left"
                        >
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400 group-hover:text-white group-hover:bg-purple-500 transition-colors">
                                    <Gamepad2 size={20} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs text-purple-300 uppercase tracking-wider">New Content</span>
                                    <span className="text-sm font-black">E-SPORTS</span>
                                </div>
                            </div>
                        </button>

                        <button
                            onClick={() => setShowVideo(true)}
                            className="group flex items-center justify-center gap-3 px-6 py-6 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 backdrop-blur-sm transition-all hover:scale-105 min-w-[200px]"
                        >
                            <Play size={20} className="text-gray-400 group-hover:text-white transition-colors" />
                            <span className="font-bold text-sm">{t('landing.btn_watch_video')}</span>
                        </button>
                    </div>

                    {/* Mockup Floating */}
                    <div className="mt-20 relative animate-fade-in delay-500 mx-auto max-w-5xl">
                        <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-purple-600 rounded-2xl blur opacity-30"></div>
                        <img
                            src="/images/sun_propoule.webp"
                            alt="ProPoule Dashboard"
                            className="relative rounded-2xl border border-white/10 shadow-2xl w-full"
                        />
                    </div>
                </div>
            </section>

            {/* 2. STATS BAR */}
            <section className="border-y border-white/5 bg-black/50 backdrop-blur-sm py-12 relative z-20 -mt-20 md:-mt-32 mx-4 md:mx-12 rounded-2xl">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-white/5">
                    <div>
                        <div className="text-4xl font-black text-white mb-1">500+</div>
                        <div className="text-sm text-gray-400 uppercase tracking-widest">{t('landing.stat_tournaments')}</div>
                    </div>
                    <div>
                        <div className="text-4xl font-black text-white mb-1">10k+</div>
                        <div className="text-sm text-gray-400 uppercase tracking-widest">{t('landing.stat_players')}</div>
                    </div>
                    <div>
                        <div className="text-4xl font-black text-white mb-1">100%</div>
                        <div className="text-sm text-gray-400 uppercase tracking-widest">{t('landing.stat_free')}</div>
                    </div>
                    <div>
                        <div className="text-4xl font-black text-white mb-1">4.9/5</div>
                        <div className="text-sm text-gray-400 uppercase tracking-widest">{t('landing.stat_rating')}</div>
                    </div>
                </div>
            </section>


            {/* 2.5 PREMIUM ECOSYSTEM SHOWCASE GRID */}
            <section className="py-24 relative overflow-hidden">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] bg-gradient-to-r from-orange-500/5 via-purple-500/5 to-green-500/5 blur-[160px] rounded-full pointer-events-none"></div>
                
                <div className="container mx-auto px-6 relative z-20">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-mono uppercase tracking-widest text-orange-400 mb-4">
                            💎 Onze Drie Premium Sport Ecososystemen
                        </span>
                        <h2 className="text-5xl md:text-6xl font-black text-white tracking-tight leading-none">
                            KIES JOUW <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-purple-400 to-green-400">WINNENDE COMBO</span>
                        </h2>
                        <p className="text-gray-400 text-lg mt-4 max-w-xl mx-auto">
                            Of je nu een voetbalpoule voor kantoor wilt, live overlays zoekt voor je Twitch stream, of een padeltoernooi organiseert: wij hebben het ultieme platform.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch max-w-7xl mx-auto">
                        
                        {/* 1. PROPOULE */}
                        <div id="propoule" className="group relative rounded-3xl border border-white/10 bg-[#0c0c0e]/85 backdrop-blur-xl p-8 flex flex-col justify-between transition-all hover:scale-[1.03] hover:border-orange-500/40 hover:shadow-[0_0_35px_rgba(249,115,22,0.15)] overflow-hidden scroll-mt-28">
                            <div className="absolute -right-16 -top-16 w-36 h-36 bg-orange-500/10 rounded-full blur-3xl group-hover:bg-orange-500/20 transition-all"></div>
                            
                            <div>
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/15 border border-orange-500/30 text-[10px] font-mono tracking-widest text-orange-400 uppercase mb-6">
                                    ⚽ CLASSIC FOOTBALL & WK POULE
                                </div>
                                <h3 className="text-4xl font-black text-white mb-2 tracking-tight group-hover:text-orange-400 transition-colors">
                                    PRO<span className="text-orange-500">POULE</span>
                                </h3>
                                <p className="text-gray-400 text-sm leading-relaxed mb-6 font-light">
                                    Hét #1 platform voor voetbaltoernooien en WK bedrijfspoules. Vervang saaie Excel-lijsten door een live dashboard met spelsfeer.
                                </p>
                                
                                <ul className="space-y-3.5 mb-8">
                                    {['Volledig geautomatiseerde speelschema\'s', 'Live poulestanden & automatische uitslagen', 'Het tactische Joker-systeem 🃏', 'Huisstijl branding & eigen logo upload'].map(item => (
                                        <li key={item} className="flex items-start gap-3 text-xs text-gray-300">
                                            <span className="text-orange-500 text-base leading-none">✔</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <Button 
                                onClick={() => navigate('/wkpoule')}
                                className="w-full py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl border-0 shadow-[0_0_20px_rgba(249,115,22,0.25)]"
                            >
                                Start WK Bedrijfspoule 🏆
                            </Button>
                        </div>

                        {/* 2. PROPULSE */}
                        <div className="group relative rounded-3xl border border-white/10 bg-[#0c0c0e]/85 backdrop-blur-xl p-8 flex flex-col justify-between transition-all hover:scale-[1.03] hover:border-purple-500/40 hover:shadow-[0_0_35px_rgba(168,85,247,0.15)] overflow-hidden">
                            <div className="absolute -right-16 -top-16 w-36 h-36 bg-purple-500/10 rounded-full blur-3xl group-hover:bg-purple-500/20 transition-all"></div>
                            
                            <div>
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/15 border border-purple-500/30 text-[10px] font-mono tracking-widest text-purple-300 uppercase mb-6">
                                    🎮 E-SPORTS & STREAM OVERLAYS
                                </div>
                                <h3 className="text-4xl font-black text-white mb-2 tracking-tight group-hover:text-purple-400 transition-colors">
                                    PRO<span className="text-purple-400">PULSE</span>
                                </h3>
                                <p className="text-gray-400 text-sm leading-relaxed mb-6 font-light">
                                    Transformeer je e-sports leagues in professionele liveshows. Realtime scorebord overlays gekoppeld met OBS Studio.
                                </p>
                                
                                <ul className="space-y-3.5 mb-8">
                                    {['< 50ms realtime broadcast latency via Supabase', 'OBS Studio & Streamlabs browserbron integratie', 'Co-caster "Ghost Admin" Ref-modus ⚔️', 'Context-aware sponsorbanners & overlays'].map(item => (
                                        <li key={item} className="flex items-start gap-3 text-xs text-gray-300">
                                            <span className="text-purple-400 text-base leading-none">✔</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <Button 
                                onClick={() => navigate('/e-sports')}
                                className="w-full py-4 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl border-0 shadow-[0_0_20px_rgba(168,85,247,0.25)]"
                            >
                                Ontdek Stream Overlays 🎥
                            </Button>
                        </div>

                        {/* 3. PRORACKET */}
                        <div id="proracket" className="group relative rounded-3xl border border-white/10 bg-[#0c0c0e]/85 backdrop-blur-xl p-8 flex flex-col justify-between transition-all hover:scale-[1.03] hover:border-green-500/40 hover:shadow-[0_0_35px_rgba(34,197,94,0.15)] overflow-hidden scroll-mt-28">
                            <div className="absolute -right-16 -top-16 w-36 h-36 bg-green-500/10 rounded-full blur-3xl group-hover:bg-green-500/20 transition-all"></div>
                            
                            <div>
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/15 border border-green-500/30 text-[10px] font-mono tracking-widest text-green-400 uppercase mb-6">
                                    🎾 PADEL, TENNIS & SQUASH
                                </div>
                                <h3 className="text-4xl font-black text-white mb-2 tracking-tight group-hover:text-green-400 transition-colors">
                                    PRO<span className="text-green-500">RACKET</span>
                                </h3>
                                <p className="text-gray-400 text-sm leading-relaxed mb-6 font-light">
                                    De premium software voor padel- en tennisclubs. Genereer dynamische ladders, speelschema\'s en baantimers.
                                </p>
                                
                                <ul className="space-y-3.5 mb-8">
                                    {['King of the Court schema generator 👑', 'Dynamische laddercompetitie & ranglijsten', 'Geïntegreerde courtflow & courtpro baansynchronisatie', 'Automatische wissel-signalen & timers'].map(item => (
                                        <li key={item} className="flex items-start gap-3 text-xs text-gray-300">
                                            <span className="text-green-500 text-base leading-none">✔</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <Button 
                                onClick={handleStart}
                                className="w-full py-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl border-0 shadow-[0_0_20px_rgba(34,197,94,0.25)]"
                            >
                                Start Padel Toernooi 🎾
                            </Button>
                        </div>

                    </div>
                </div>
            </section>

            {/* 3. GAME MODES - Visual Grid */}
            <section className="py-32 relative">
                <div className="container mx-auto px-6">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-20">
                        <div className="max-w-2xl">
                            <h2 className="text-orange-500 font-bold tracking-widest uppercase mb-4 text-sm">{t('landing.modes_title_small')}</h2>
                            <h3 className="text-5xl md:text-6xl font-black text-white leading-none">
                                {t('landing.modes_title_large_1')} <br />{t('landing.modes_title_large_2')}
                            </h3>
                        </div>
                        <p className="text-gray-400 text-lg max-w-md mt-6 md:mt-0">
                            {t('landing.modes_desc')}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-12 gap-6 h-[800px] md:h-[600px]">
                        {/* PADEL / KING OF THE COURT (Large) */}
                        <div className="md:col-span-8 group relative rounded-3xl overflow-hidden border border-white/10 bg-gray-900 cursor-pointer">
                            <div className="absolute inset-0 bg-[url('/images/padel.webp')] bg-cover bg-center transition-transform duration-700 group-hover:scale-110 opacity-60 group-hover:opacity-40"></div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent"></div>
                            <div className="absolute bottom-0 left-0 p-10">
                                <div className="w-16 h-16 bg-orange-600 rounded-2xl flex items-center justify-center mb-6">
                                    <Crown size={32} className="text-white" />
                                </div>
                                <h4 className="text-4xl font-bold mb-3">{t('landing.mode_king_title')}</h4>
                                <p className="text-gray-300 max-w-md text-lg">{t('landing.mode_king_desc')}</p>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="md:col-span-4 flex flex-col gap-6">
                            {/* TOURNAMENT */}
                            <div className="flex-1 group relative rounded-3xl overflow-hidden border border-white/10 bg-gray-900 cursor-pointer">
                                <div className="absolute inset-0 bg-[url('/images/soccer_1.webp')] bg-cover bg-center transition-transform duration-700 group-hover:scale-110 opacity-60 group-hover:opacity-40"></div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
                                <div className="absolute bottom-0 left-0 p-8">
                                    <h4 className="text-2xl font-bold mb-2 flex items-center gap-3"><Trophy className="text-blue-500" /> {t('landing.mode_tour_title')}</h4>
                                    <p className="text-gray-400 text-sm">{t('landing.mode_tour_desc')}</p>
                                </div>
                            </div>

                            {/* LADDER */}
                            <div className="flex-1 group relative rounded-3xl overflow-hidden border border-white/10 bg-gray-900 cursor-pointer">
                                <div className="absolute inset-0 bg-[url('/images/darts.webp')] bg-cover bg-center transition-transform duration-700 group-hover:scale-110 opacity-60 group-hover:opacity-40"></div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
                                <div className="absolute bottom-0 left-0 p-8">
                                    <h4 className="text-2xl font-bold mb-2 flex items-center gap-3"><Target className="text-purple-500" /> {t('landing.mode_ladder_title')}</h4>
                                    <p className="text-gray-400 text-sm">{t('landing.mode_ladder_desc')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>


            {/* 4. MOBILE SHOWCASE */}
            <section className="py-24 bg-[#050505] overflow-hidden">
                <div className="container mx-auto px-6 relative">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-orange-600/20 blur-[120px] rounded-full pointer-events-none"></div>

                    <div className="flex flex-col md:flex-row items-center gap-20">
                        <div className="flex-1 space-y-8 relative z-10">
                            <h2 className="text-5xl md:text-6xl font-black leading-tight">
                                {t('landing.mobile_title_1')} <br />
                                {t('landing.mobile_title_2')}
                            </h2>
                            <div className="space-y-6">
                                <FeatureItem
                                    icon={<Smartphone />}
                                    title={t('landing.feat_app_title')}
                                    desc={t('landing.feat_app_desc')}
                                />
                                <FeatureItem
                                    icon={<Zap />}
                                    title={t('landing.feat_live_title')}
                                    desc={t('landing.feat_live_desc')}
                                />
                                <FeatureItem
                                    icon={<Shield />}
                                    title={t('landing.feat_admin_title')}
                                    desc={t('landing.feat_admin_desc')}
                                />
                            </div>
                        </div>

                        <div className="flex-1 relative z-10 perspective-1000">
                            <div className="relative mx-auto w-[300px] animate-float">
                                <img src="/images/Esports_FC26.webp" alt="Mobile Screen" className="relative z-20 rounded-[3rem] border-8 border-gray-800 shadow-2xl" />
                                {/* Glow behind phone */}
                                <div className="absolute inset-0 bg-orange-500 blur-3xl opacity-40 -z-10"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. PRICING - DARK & BOLD */}
            <section className="py-32 relative bg-[#0a0a0a]" id="pricing">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-20">
                        <h2 className="text-4xl text-white font-black mb-4 uppercase tracking-wide">{t('landing.pricing_title')}</h2>
                        <div className="w-24 h-1 bg-orange-500 mx-auto rounded-full"></div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto items-center">

                        {/* Free Tier */}
                        <PricingCard
                            title={t('landing.price_starter')}
                            price="€0"
                            period={t('landing.price_starter_period')}
                            desc={t('landing.price_starter_desc')}
                            features={['1 Toernooi', 'Max 8 Teams', 'Basis Schema']}
                        />

                        {/* PRO - Featured */}
                        <div className="relative transform md:scale-110 z-10">
                            <div className="absolute -inset-1 bg-gradient-to-br from-orange-500 to-red-600 rounded-3xl blur opacity-75"></div>
                            <div className="relative bg-[#151515] p-10 rounded-2xl border border-white/10 h-full flex flex-col">
                                <div className="absolute top-0 right-10 -translate-y-1/2 bg-orange-600 text-white font-bold px-4 py-1 rounded-full text-sm uppercase tracking-wider shadow-lg">
                                    {t('landing.price_pro_pop')}
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-2">{t('landing.price_pro')}</h3>
                                <div className="flex items-baseline gap-1 mb-6">
                                    <span className="text-5xl font-black text-white">€9,95</span>
                                    <span className="text-gray-400">{t('landing.price_pro_period')}</span>
                                </div>
                                <p className="text-gray-400 mb-8 pb-8 border-b border-white/10">
                                    {t('landing.price_pro_desc')}
                                </p>
                                <ul className="space-y-4 mb-10 flex-1">
                                    {['Onbeperkt Toernooien', 'Onbeperkt Teams', 'King of the Court 👑', 'Ladder Competitie', 'Geen Reclame'].map(f => (
                                        <li key={f} className="flex items-center gap-3 text-gray-200 font-medium">
                                            <CheckCircle size={20} className="text-orange-500 shrink-0" /> {f}
                                        </li>
                                    ))}
                                </ul>
                                <Button
                                    onClick={handleStart}
                                    className="w-full py-6 text-lg font-bold bg-white text-black hover:bg-gray-200 border-0 rounded-xl"
                                >
                                    {t('landing.btn_trial')}
                                </Button>
                            </div>
                        </div>

                        {/* Club Tier */}
                        <PricingCard
                            title={t('landing.price_club')}
                            price="€24,95"
                            period={t('landing.price_club_period')}
                            desc={t('landing.price_club_desc')}
                            features={['Alles uit Pro', '5 Beheerders', 'Whitelabel Branding', 'Facturatie']}
                            buttonVariant="outline"
                            buttonLabel={t('landing.btn_contact')}
                            customOnClick={() => window.location.href = 'mailto:info@claritysolutions.cloud?subject=Interesse%20in%20Club%20Pakket'}
                        />

                    </div>
                </div>
            </section>

            {/* 6. GIANT SEO CLUSTER & FAQ SECTION (The Organic Growth Engine) */}
            <section className="py-24 bg-[#050505] border-t border-white/5">
                <div className="container mx-auto px-6 max-w-4xl">
                    
                    {/* SEO Heading Block */}
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-black text-white mb-6">Alles wat je moet weten over ProPoule</h2>
                        <p className="text-gray-400 leading-relaxed">
                            Ben je op zoek naar een <strong>gratis toernooi generator</strong> of wil je een professioneel <strong>toernooi schema maken</strong>? ProPoule is het ultieme platform voor clubs, scholen en vriendengroepen om binnen enkele minuten een volledig geautomatiseerde competitie op te zetten.
                        </p>
                    </div>

                    {/* FAQ Accordion */}
                    <div className="space-y-4 mb-20">
                        {[
                            {
                                q: "Hoe kan ik een toernooi schema maken met ProPoule?",
                                a: "Het aanmaken van een schema is kinderlijk eenvoudig. Registreer je gratis, voer de namen van de teams of spelers in, en kies je speltype (Poule, Knock-out, of Ladder). Ons algoritme genereert direct een eerlijk speelschema met de juiste tijden en veldindeling."
                            },
                            {
                                q: "Is ProPoule écht een gratis toernooi generator?",
                                a: "Ja, voor kleine toernooien (tot 8 teams) is ProPoule volledig gratis te gebruiken. Je krijgt toegang tot live scores, standen en het online dashboard zonder een cent te betalen. Voor grotere toernooien en extra functies zoals 'King of the Court' bieden wij zeer betaalbare upgrades."
                            },
                            {
                                q: "Kan ik de live tussenstanden delen met deelnemers?",
                                a: "Absoluut! Elk toernooi krijgt een unieke 'Live Link'. Deelnemers kunnen deze openen op hun mobiele telefoon en zien direct wie er moet spelen en wat de actuele stand in de poule is. Geen gedoe meer met uitgeprinte A4'tjes!"
                            },
                            {
                                q: "Welke sporten worden ondersteund?",
                                a: "Hoewel ProPoule veel gebruikt wordt als <strong>voetbal toernooi planner</strong>, is de software universeel. Het werkt perfect voor padel, tennis, darts, hockey, volleybal en zelfs e-sports toernooien zoals EA FC26."
                            }
                        ].map((faq, i) => (
                            <details key={i} className="group bg-[#0a0a0a] border border-white/10 rounded-xl p-6 [&_summary::-webkit-details-marker]:hidden cursor-pointer hover:border-orange-500/30 transition-colors">
                                <summary className="flex items-center justify-between text-lg font-bold text-white group-open:text-orange-400 transition-colors">
                                    <span>{faq.q}</span>
                                    <span className="text-2xl text-gray-500 group-open:rotate-45 transition-transform">+</span>
                                </summary>
                                <div className="mt-4 text-gray-400 leading-relaxed text-sm border-t border-white/5 pt-4" dangerouslySetInnerHTML={{ __html: faq.a }} />
                            </details>
                        ))}
                    </div>

                    {/* Hidden SEO Text Cluster for Google Crawler */}
                    <div className="bg-[#0a0a0a] border border-white/5 rounded-2xl p-8 text-[11px] text-gray-600 leading-relaxed text-center">
                        <h3 className="font-bold uppercase text-gray-500 mb-2 tracking-wider">Populaire Zoektermen</h3>
                        <p>
                            Toernooi schema maken gratis • online toernooi generator • voetbal schema maken • poule generator excel alternatief • padel ladder competitie app • darts toernooi planner • king of the court schema • gratis toernooi software • wedstrijdplanner sportclub • poule indeling maken online • automatisch speelschema genereren • bracket maker nederlands • toernooi organiseren app • propoule • propulse • proracket • pro-racket • pro-pulse • twitch scoreboard overlay.
                        </p>
                        <p className="mt-4">
                            Of je nu een WK bedrijfspoule wil organiseren met <strong>ProPoule</strong>, professionele e-sports toernooien en realtime stream overlays wil inzetten met <strong>ProPulse</strong>, of dynamische padel- en tennisladders en King of the Court schema's wilt plannen met <strong>ProRacket</strong>: wij leveren de allerbeste sport- en toernooisoftware ter wereld. Probeer het vandaag gratis!
                        </p>
                    </div>                </div>
            </section>

            <footer className="py-12 border-t border-white/10 bg-black text-center text-gray-600">
                <p>&copy; 2024 ProPoule. {t('landing.footer_rights')}</p>
                <div className="flex justify-center gap-6 mt-4 text-sm">
                    <button onClick={() => navigate('/privacy')} className="hover:text-gray-400 transition-colors">{t('landing.privacy')}</button>
                    <button onClick={() => navigate('/terms')} className="hover:text-gray-400 transition-colors">{t('landing.terms')}</button>
                </div>
            </footer>

            <Modal isOpen={showVideo} onClose={() => setShowVideo(false)} title="Promo Video">
                <div className="relative w-full pb-[56.25%] bg-black rounded-lg overflow-hidden shadow-2xl">
                    <iframe
                        title="ProPoule Promo"
                        src="https://veelvo.org/videos/embed/7GZ5PnLn2EnJdRUm1BehF7"
                        allow="fullscreen"
                        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                        className="absolute top-0 left-0 w-full h-full border-0"
                    ></iframe>
                </div>
            </Modal>
        </div>
    )
}

// Components
const FeatureItem = ({ icon, title, desc }) => (
    <div className="flex items-start gap-5">
        <div className="p-4 bg-white/5 rounded-2xl text-orange-500 border border-white/5">
            {icon}
        </div>
        <div>
            <h4 className="text-xl font-bold text-white mb-1">{title}</h4>
            <p className="text-gray-400 leading-relaxed">{desc}</p>
        </div>
    </div>
)

const PricingCard = ({ title, price, period, desc, features, buttonVariant, buttonLabel, customOnClick }) => {
    const navigate = useNavigate()

    const handleClick = () => {
        if (customOnClick) {
            customOnClick()
        } else {
            navigate('/login')
        }
    }

    return (
        <div className="bg-[#101010] p-8 rounded-3xl border border-white/5 hover:border-white/10 transition-colors h-full flex flex-col">
            <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
            <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-black text-white">{price}</span>
                <span className="text-gray-500">{period}</span>
            </div>
            <p className="text-gray-500 mb-8 pb-8 border-b border-white/5">{desc}</p>
            <ul className="space-y-4 mb-10 flex-1">
                {features.map(f => (
                    <li key={f} className="flex items-center gap-3 text-gray-400 text-sm">
                        <CheckCircle size={18} className="text-gray-600 shrink-0" /> {f}
                    </li>
                ))}
            </ul>
            <Button
                onClick={handleClick}
                variant={buttonVariant || "ghost"}
                className={`w-full py-6 rounded-xl border-white/10 ${!buttonVariant ? 'bg-white/5 hover:bg-white/10 text-white' : ''}`}
            >
                {buttonLabel || `Kies ${title}`}
            </Button>
        </div>
    )
}
